'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Check } from 'lucide-react'
import { leadSourceFields } from '../lib/leadSource'
import { submitLead } from '../lib/leadCapture'
import './service-survey.css'

const DIAL_CODES = ['+1', '+44', '+61', '+91', '+971', '+92', '+234', '+353', '+49', '+33', '+39', '+34', '+27', '+880', '+63']

// GoHighLevel (LeadConnector) inbound webhook for every service-page form. Leads
// normally reach it through the GHL Prime backend (lib/leadCapture.js); the
// browser only posts here directly if the backend can't be reached.
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/j53xn6YJHwIdPImV00rn/webhook-trigger/a325877a-48bf-4f92-8bf7-61aece1d7cb9'

// One short step on every service page: the contact fields plus that page's
// "what do you need" box from the config (data/serviceForms.js). The survey
// questions still listed there (role, budget, scope, how found) are no longer
// asked, owner's call 2026-10-20; their payload keys are sent empty.
export default function ServiceSurveyForm({ form, slug }) {
  const [data, setData] = useState({ dialCode: '+1' })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const set = (name, value) => setData((d) => ({ ...d, [name]: value }))
  const contactFields = form.steps.flatMap((s) => s.fields || [])
  const message = form.steps.map((s) => s.textarea).find(Boolean)

  const valid = contactFields.every((f) => !f.required || String(data[f.name] || '').trim())

  // One fixed schema for EVERY service form. Fields a given form does not collect
  // are sent as empty strings, so the backend / email template can map once.
  const buildPayload = () => {
    const d = data
    const v = (k) => (d[k] == null ? '' : String(d[k]))
    const lines = []
    for (const s of form.steps) {
      for (const q of s.questions || []) {
        if (d[q.name]) lines.push(`${q.label} ${d[q.name]}`)
      }
      if (s.textarea && d[s.textarea.name]) lines.push(`${s.textarea.label}: ${d[s.textarea.name]}`)
    }
    return {
      // Contact same on every form
      name: v('name'),
      email: v('email'),
      phone: `${d.dialCode || ''} ${d.phone || ''}`.trim(),
      business: v('business'),
      // "About you" only one of these is filled per form, the rest stay empty
      role: v('role'),
      business_type: v('bizType'),
      stage: v('stage'),
      app_type: v('appType'),
      // Free-text needs (all forms)
      needs: v('needsDetail'),
      // Budget / scope subset filled per form, rest empty
      budget: v('budget'),
      sub_accounts: v('subaccounts'),
      lead_volume: v('leadVolume'),
      coverage: v('coverage'),
      // T-12.5 attribution. The question isn't asked (removed 2026-10-20);
      // the lead source is captured automatically (lib/leadSource.js).
      how_found: v('howFound'),
      ...leadSourceFields(),
      // Meta same on every form
      service: slug,
      source: 'Service page survey',
      details: lines.join('\n'),
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      submitted_at: new Date().toISOString(),
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!valid || submitting) return
    const payload = buildPayload()
    setSubmitting(true)
    const { ok } = await submitLead({ form: 'service', payload, fallbackWebhookUrl: WEBHOOK_URL })
    if (!ok) console.error('Lead submit failed: neither the API nor the webhook accepted it')
    setSubmitting(false)
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <div className="svf-card">
        <div className="svf-body svf-success">
          <div className="svf-success-icon"><Check size={26} /></div>
          <h3 className="svf-success-title">{form.success.title}</h3>
          <p className="svf-success-sub">{form.success.subtitle}</p>
          <Link href={form.success.ctaTo || '/booking'} className="svf-cal-btn">
            {form.success.ctaLabel} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    )
  }


  return (
    <div className="svf-card">
      <div className="svf-head">
        {form.titlePill ? <span className="svf-pill">{form.titlePill}</span> : null}
        {form.titleSub ? <p className="svf-subtitle">{form.titleSub}</p> : null}
      </div>

      <form className="svf-body" onSubmit={handleSubmit} noValidate>
        {contactFields.map((f) =>
          f.type === 'phone' ? (
            <div className="svf-group" key={f.name}>
              <label className="svf-label" htmlFor={`svf-${f.name}`}>{f.label}{f.required ? <span className="svf-req">*</span> : null}</label>
              <div className="svf-phone-row">
                <select
                  className="svf-country"
                  value={data.dialCode}
                  onChange={(e) => set('dialCode', e.target.value)}
                  aria-label="Country dialing code"
                >
                  {DIAL_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  id={`svf-${f.name}`}
                  className="svf-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 000-0000"
                  value={data[f.name] || ''}
                  onChange={(e) => set(f.name, e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="svf-group" key={f.name}>
              <label className="svf-label" htmlFor={`svf-${f.name}`}>{f.label}{f.required ? <span className="svf-req">*</span> : null}</label>
              <input
                id={`svf-${f.name}`}
                className="svf-input"
                type={f.type === 'email' ? 'email' : 'text'}
                autoComplete={f.type === 'email' ? 'email' : f.name === 'name' ? 'name' : f.name === 'business' ? 'organization' : undefined}
                placeholder={f.placeholder || ''}
                value={data[f.name] || ''}
                onChange={(e) => set(f.name, e.target.value)}
              />
            </div>
          ),
        )}

        {message ? (
          <div className="svf-group">
            <label className="svf-label" htmlFor={`svf-${message.name}`}>
              {message.label} <span className="svf-optional">(optional)</span>
            </label>
            <textarea
              id={`svf-${message.name}`}
              className="svf-textarea"
              placeholder={message.placeholder || ''}
              value={data[message.name] || ''}
              onChange={(e) => set(message.name, e.target.value)}
            />
          </div>
        ) : null}

        <div className="svf-nav end">
          <button type="submit" className="svf-next" disabled={!valid || submitting}>
            {submitting ? 'Sending...' : (form.submitLabel || 'Submit')} <ArrowRight size={16} />
          </button>
        </div>
      </form>
    </div>
  )
}
